import React from 'react';
import {
  ArrowUpIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { 
  DocumentVersion, 
  PromotionDetails, 
  PromotionStatus, 
  PromotionStep 
} from '../types/documentVersion.types';

interface PromoteVersionModalProps {
  isOpen: boolean;
  versionToPromote: DocumentVersion | null;
  promotionDetails: PromotionDetails | null;
  promotionStatus: PromotionStatus;
  promotionStep: PromotionStep;
  promotionError: string | null;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export const PromoteVersionModal: React.FC<PromoteVersionModalProps> = ({
  isOpen,
  versionToPromote,
  promotionDetails,
  promotionStatus,
  promotionStep,
  promotionError,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen || !versionToPromote || !promotionDetails) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-4">
        <div className="fixed inset-0 transition-opacity lex-overlay-enter">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl lex-modal-enter">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 sm:mx-0 sm:h-10 sm:w-10">
                <ArrowUpIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Promote Version to Active
                </h3>
                
                {/* Enhanced: FROM → TO Visualization */}
                <div className="mt-4 space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Version Change Summary</h4>
                    <div className="space-y-3">
                      {promotionDetails.fromVersion && (
                        <div className="flex items-center space-x-3">
                          <span className="text-red-600 dark:text-red-400 text-sm font-medium">From:</span>
                          <div className="flex-1">
                            <span className="font-mono bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded text-sm">
                              {promotionDetails.fromVersion.version_number}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(promotionDetails.fromVersion.created_at).toLocaleDateString()} • {Math.round((promotionDetails.fromVersion.size_bytes || 0) / 1024)} KB
                            </p>
                          </div>
                        </div>
                      )}
                      {promotionDetails.toVersion && (
                        <div className="flex items-center space-x-3">
                          <span className="text-green-600 dark:text-green-400 text-sm font-medium">To:</span>
                          <div className="flex-1">
                            <span className="font-mono bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded text-sm">
                              {promotionDetails.toVersion.version_number}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(promotionDetails.toVersion.created_at).toLocaleDateString()} • {Math.round((promotionDetails.toVersion.size_bytes || 0) / 1024)} KB
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Enhanced: Version Metadata */}
                  {promotionDetails.toVersion && (
                    <div className="bg-lex-accent-soft p-4 rounded-lg border-l-4 border-lex-accent">
                      <h4 className="text-sm font-medium text-lex-accent mb-2">Version Details</h4>
                      <div className="space-y-2 text-sm text-lex-accent">
                        <p><strong>File:</strong> {promotionDetails.toVersion.original_name}</p>
                        {promotionDetails.toVersion.changes_description && (
                          <p><strong>Changes:</strong> {promotionDetails.toVersion.changes_description}</p>
                        )}
                        <p><strong>Uploaded by:</strong> {promotionDetails.toVersion.uploaded_by_user_id}</p>
                      </div>
                    </div>
                  )}

                  {/* Enhanced: Legal Warning */}
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border-l-4 border-yellow-400">
                    <div className="flex items-start space-x-2">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-200">Important Notice</h4>
                        <p className="text-sm text-yellow-800 dark:text-yellow-300 mt-1">
                          This action will change the active version but will not delete any history. All versions remain accessible and no files will be modified.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Enhanced: Real-time Status Updates */}
                  {promotionStatus === 'promoting' && (
                    <div className="bg-lex-accent-soft p-4 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-lex-accent"></div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-lex-accent">
                            {promotionStep === 'validation' && 'Validating version...'}
                            {promotionStep === 'promotion' && 'Promoting version...'}
                            {promotionStep === 'completion' && 'Completing promotion...'}
                          </p>
                          <div className="mt-2 flex space-x-1">
                            <div className={`h-1 flex-1 rounded ${promotionStep === 'validation' ? 'bg-lex-accent' : 'bg-lex-accent-soft'}`}></div>
                            <div className={`h-1 flex-1 rounded ${promotionStep === 'promotion' ? 'bg-lex-accent' : 'bg-lex-accent-soft'}`}></div>
                            <div className={`h-1 flex-1 rounded ${promotionStep === 'completion' ? 'bg-lex-accent' : 'bg-lex-accent-soft'}`}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Enhanced: Success State */}
                  {promotionStatus === 'success' && promotionDetails.toVersion && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border-l-4 border-green-500">
                      <div className="flex items-center space-x-2">
                        <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <div>
                          <h4 className="text-sm font-medium text-green-900 dark:text-green-200">Promotion Successful</h4>
                          <p className="text-sm text-green-800 dark:text-green-300">
                            Version {promotionDetails.toVersion.version_number} is now the active version.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Enhanced: Error State */}
                  {promotionStatus === 'error' && promotionError && (
                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border-l-4 border-red-500">
                      <div className="flex items-center space-x-2">
                        <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <div>
                          <h4 className="text-sm font-medium text-red-900 dark:text-red-200">Promotion Failed</h4>
                          <p className="text-sm text-red-800 dark:text-red-300">{promotionError}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onConfirm}
              disabled={promotionStatus === 'promoting' || promotionStatus === 'success'}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {promotionStatus === 'promoting' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Promoting...
                </>
              ) : promotionStatus === 'success' ? (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Promoted
                </>
              ) : (
                'Promote'
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={promotionStatus === 'promoting'}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};